import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/MeterReadingsService';
import { MeterReading } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useMeterReading = (meterReadingId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const meterReadingQuery = useQuery({
        queryKey: ['meter-reading', meterReadingId],
        queryFn: () => fetch(meterReadingId!),
        enabled: !!meterReadingId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: MeterReading) => {
            const { created: _created, updated: _updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            notifySuccess('Meter reading created');
            queryClient.invalidateQueries({ queryKey: ['meter-readings'] });
        },
        onError: notifyError
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: MeterReading) => {
            if (!meterReadingId) throw new Error('meterReadingId is required parameter');
            const { created: _created, updated: _updated, ...rest } = values;
            return update(meterReadingId, rest);
        },
        onSuccess: () => {
            notifySuccess('Meter reading updated');
            queryClient.invalidateQueries({ queryKey: ['meter-readings'] });
            queryClient.invalidateQueries({ queryKey: ['meter-reading', meterReadingId] });
        },
        onError: notifyError
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!meterReadingId) throw new Error('meterReadingId is required parameter');
            return deleteRecord(meterReadingId);
        },
        onSuccess: () => {
            notifySuccess('Meter reading deleted');
            queryClient.invalidateQueries({ queryKey: ['meter-readings'] });
        },
        onError: notifyError
    });

    return {
        record: meterReadingQuery.data,
        loading: meterReadingQuery.isLoading,
        fetchRecord: meterReadingQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};